// ========== 主题提供器 ==========
// 将当前主题应用到 DOM (CSS 变量) 和 Ant Design 组件

import React, { useEffect, useMemo } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { useThemeStore } from '@/store/useThemeStore';
import { themeToCSSVars } from '@/themes';

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useThemeStore(s => s.theme);

  // 将主题色写入 CSS 变量到 :root, 并设置 data-theme 属性
  useEffect(() => {
    const vars = themeToCSSVars(theme);
    const root = document.documentElement;
    root.setAttribute('data-theme', theme.id);
    // 「@uiw/react-md-editor / react-markdown-preview」只认 data-color-mode，
    // 不认本站的 data-theme；缺省时它走系统 prefers-color-scheme（多为 light），
    // 会出现暗色主题下编辑器白底 + theme.css 强制白字 → 白底白字看不见。
    // 因此这里把本站主题同步成库识别的明暗模式。
    root.setAttribute('data-color-mode', theme.antdAlgorithm === 'dark' ? 'dark' : 'light');
    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, [theme]);

  // Ant Design 算法映射
  const algorithm = useMemo(() => {
    switch (theme.antdAlgorithm) {
      case 'dark': return antdTheme.darkAlgorithm;
      case 'compact': return antdTheme.compactAlgorithm;
      default: return antdTheme.defaultAlgorithm;
    }
  }, [theme]);

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm,
        token: {
          colorPrimary: theme.colors.accent,
          colorSuccess: theme.colors.success,
          colorWarning: theme.colors.warning,
          colorError: theme.colors.danger,
          // 容器/浮层用不透明中性色（bgContainer），避免半透明白叠在蓝黑底上呈蓝紫
          colorBgElevated: theme.colors.bgContainer,
          colorBgContainer: theme.colors.bgContainer,
          colorTextBase: theme.colors.textPrimary,
          colorTextSecondary: theme.colors.textSecondary,
          borderRadius: 8,
          ...theme.antdToken,
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

export default ThemeProvider;
